import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

const MyPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px',
      md: '6px',
      lg: '8px',
      xl: '12px',
    },
    brand: {
      50: '#EFF8FF',
      100: '#DBEFFE',
      200: '#C0E4FD',
      300: '#94D4FC',
      400: '#61BBF9',
      500: '#3D9DF4',
      600: '#2781E9',
      700: '#1F6AD6',
      800: '#1E52A6',
      900: '#1F4A89',
      950: '#182E53',
    },
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
      950: '#030712',
    },
  },
  semantic: {
    primary: {
      50: '{brand.50}',
      100: '{brand.100}',
      200: '{brand.200}',
      300: '{brand.300}',
      400: '{brand.400}',
      500: '{brand.500}',
      600: '{brand.600}',
      700: '{brand.700}',
      800: '{brand.800}',
      900: '{brand.900}',
      950: '{brand.950}',
    },
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.500}',
      offset: '2px',
      shadow: 'none',
    },
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      sm: {
        fontSize: '0.75rem',
        paddingX: '0.625rem',
        paddingY: '0.375rem',
      },
      lg: {
        fontSize: '1rem',
        paddingX: '0.875rem',
        paddingY: '0.625rem',
      },
      borderRadius: '{border.radius.sm}',
      focusRing: {
        width: '2px',
        style: 'solid',
        color: '{primary.200}',
        offset: '0',
        shadow: '0 0 0 1px {primary.500}',
      },
    },
    list: {
      padding: '0.25rem',
      gap: '0.125rem',
      header: {
        padding: '0.5rem 0.75rem 0.25rem',
      },
      option: {
        padding: '0.5rem 0.75rem',
        borderRadius: '{border.radius.sm}',
      },
      optionGroup: {
        padding: '0.5rem 0.75rem',
        fontWeight: '600',
      },
    },
    navigation: {
      list: {
        padding: '0.25rem',
        gap: '0.125rem',
      },
      item: {
        padding: '0.5rem 0.75rem',
        borderRadius: '{border.radius.sm}',
        gap: '0.5rem',
      },
      submenuLabel: {
        padding: '0.5rem 0.75rem',
        fontWeight: '600',
      },
    },
    overlay: {
      select: {
        borderRadius: '{border.radius.md}',
      },
      popover: {
        borderRadius: '{border.radius.md}',
        padding: '0.75rem',
      },
      modal: {
        borderRadius: '{border.radius.lg}',
        padding: '1.25rem',
      },
    },
    colorScheme: {
      light: {
        surface: {
          0: '#FFFFFF',
          50: '{gray.50}',
          100: '{gray.100}',
          200: '{gray.200}',
          300: '{gray.300}',
          400: '{gray.400}',
          500: '{gray.500}',
          600: '{gray.600}',
          700: '{gray.700}',
          800: '{gray.800}',
          900: '{gray.900}',
          950: '{gray.950}',
        },
        primary: {
          color: '{primary.800}',
          contrastColor: '#FFFFFF',
          hoverColor: '{primary.900}',
          activeColor: '{primary.950}',
        },
        highlight: {
          background: '{primary.50}',
          focusBackground: '{primary.100}',
          color: '{primary.800}',
          focusColor: '{primary.900}',
        },
        text: {
          color: '{gray.900}',
          hoverColor: '{gray.950}',
          mutedColor: '{gray.500}',
          hoverMutedColor: '{gray.600}',
        },
        formField: {
          background: '#FFFFFF',
          disabledBackground: '{gray.100}',
          filledBackground: '{gray.50}',
          filledHoverBackground: '{gray.50}',
          filledFocusBackground: '#FFFFFF',
          borderColor: '{gray.300}',
          hoverBorderColor: '{gray.400}',
          focusBorderColor: '{primary.800}',
          color: '{gray.900}',
          disabledColor: '{gray.500}',
          placeholderColor: '{gray.400}',
          iconColor: '{gray.500}',
        },
        content: {
          background: '#FFFFFF',
          hoverBackground: '{gray.50}',
          borderColor: '{gray.200}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}',
        },
      },
    },
  },
  components: {
    button: {
      root: {
        borderRadius: '{border.radius.sm}',
        paddingX: '1rem',
        paddingY: '0.5rem',
        iconOnlyWidth: '2.5rem',
        label: {
          fontWeight: '600',
        },
        sm: {
          fontSize: '0.75rem',
          paddingX: '0.75rem',
          paddingY: '0.375rem',
          iconOnlyWidth: '2rem',
        },
        lg: {
          fontSize: '1rem',
          paddingX: '1.25rem',
          paddingY: '0.625rem',
          iconOnlyWidth: '3rem',
        },
      },
    },
    dialog: {
      root: {
        borderRadius: '{border.radius.lg}',
      },
      header: {
        padding: '1.25rem',
        fontSize: '1.125rem',
      },
      content: {
        padding: '0 1.25rem 1.25rem',
      },
      footer: {
        padding: '0 1.25rem 1.25rem',
      },
    },
  },
} as any);

export default MyPreset;
